import { Component, ElementRef, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material';
import { forkJoin, Observable, timer } from 'rxjs';
import { take } from 'rxjs/operators';
import { Drug, Gender, TemperatureUnit, UpdatePatientInput } from 'src/API';
import { Assessment } from 'src/app/core/api/assessment.service';
import { Patient, PatientService } from 'src/app/core/api/patient.service';
import { AccessState } from 'src/app/core/schemas/subscription-plan.schema';
import { SubscriptionPlanAccessService } from 'src/app/core/services/subscription-plan-access.service';
import { ClinicSetupService } from 'src/app/logged-in-navbar/clinic-setup-modal/clinic-setup.service';
import { LayoutsService } from 'src/app/logged-in-navbar/clinic-setup-modal/layouts/layouts.service';
import {
  Treatment,
  TreatmentsService
} from '../../../core/remote-json/treatments/treatments.service';
import { DrugbankLookupService } from '../../../shared/medication-lookup/drugbank-lookup.service';
import {
  AIScribeUsageData,
  ApplyOutput,
  DewsScreeningExtraction,
  HistoryExtraction,
  PatientFileExtraction,
  SavedAIScribeData,
  SymptomsExtraction
} from '../../../shared/consult-forms/assessment-ai-scribe/assessment-ai-scribe.model';
import {
  OverwriteChoice,
  ScribeOverwriteDialogComponent
} from '../../../shared/consult-forms/assessment-ai-scribe/components/scribe-overwrite-dialog/scribe-overwrite-dialog.component';
import { DryEyeAssessmentFormGroup } from '../../../shared/consult-forms/dry-eye-forms/dry-eye-assessment-form/dry-eye-assessment-form.model';
import { DryEyeFormGroup } from '../../../shared/consult-forms/dry-eye-forms/dry-eye-form/dry-eye-form.model';
import { DryEyeMedicalHistoryFormGroup } from '../../../shared/consult-forms/dry-eye-forms/dry-eye-medical-history-form/dry-eye-medical-history-form.model';
import { WeatherFormGroup } from '../../../shared/consult-forms/dry-eye-forms/weather/weather-form.model';
import { symptomMethodKey } from '../../../shared/form.model';
import { SchemaService } from '../../../shared/symptoms/services/schema/schema.service';
import { SymptomTypes } from '../../../shared/symptoms/symptom.model';

@Component({
  selector: 'csi-ai-scribe-overlay',
  templateUrl: './ai-scribe-overlay.component.html',
  styleUrls: ['./ai-scribe-overlay.component.scss']
})
export class AiScribeOverlayComponent implements OnInit {
  @ViewChild('scribePanel', { static: false }) private scribePanel: ElementRef;

  @Input() medicalHistoryFormGroup: DryEyeMedicalHistoryFormGroup;
  @Input() symptomsAndSignsFormGroup: DryEyeFormGroup;
  @Input() weatherFormGroup: WeatherFormGroup;
  @Input() assessmentFormGroup: DryEyeAssessmentFormGroup;
  @Input() patient: Patient;
  @Input() assignedDoctor: { firstName?: string; lastName?: string } | null = null;
  @Input() assessment: Assessment;
  @Input() saveFn: () => Observable<Assessment>;
  @Input() isLocked: boolean;

  public assessmentMethod: any = null;
  public isScribeOpen = false;
  public isScribeExpanded = false;
  public isScribeClosing = false;
  public isScribeMinimized = false;
  public scribeStep: string = 'input';
  public cachedScribeUsage: AIScribeUsageData | null = null;
  public pendingScribeAttachment: any = null;
  public isDialogOpen = false;
  public savedAssessmentId: string | null = null;

  get clinicId(): string {
    return this.clinicSetupService.clinicId;
  }

  get aiScribeEnabledSections(): string[] {
    const setup = this.clinicSetupService.aiScribeSetup;
    if (!setup || !setup.sections) {
      return ['findings', 'patientProfile', 'history', 'symptoms', 'patientFile', 'assessment'];
    }
    return setup.sections;
  }

  get showAiScribe(): boolean {
    if (!this.assessmentMethod) {
      return false;
    }
    const schema = this.subscriptionPlanAccessService.flattenedSubscriptionPlanSchema;
    const featureEntry = schema['features.AI_SCRIBE'];
    const accessState = featureEntry && featureEntry.accessState;
    if (accessState === AccessState.PRO_LOCKED || accessState === AccessState.HIDDEN) {
      return false;
    }
    const setup = this.clinicSetupService.aiScribeSetup;
    if (!setup || setup.devEnabled === false) {
      return false;
    }
    return !!(setup && setup.enabled);
  }

  get showAiScribeLocked(): boolean {
    const schema = this.subscriptionPlanAccessService.flattenedSubscriptionPlanSchema;
    const featureEntry = schema['features.AI_SCRIBE'];
    const accessState = featureEntry && featureEntry.accessState;
    if (accessState === AccessState.HIDDEN) {
      return false;
    }
    return accessState === AccessState.PRO_LOCKED;
  }

  constructor(
    private patientService: PatientService,
    private dialog: MatDialog,
    private clinicSetupService: ClinicSetupService,
    private layoutsService: LayoutsService,
    private subscriptionPlanAccessService: SubscriptionPlanAccessService,
    private treatmentsService: TreatmentsService,
    private drugbankLookupService: DrugbankLookupService,
    private schemaService: SchemaService
  ) {}

  ngOnInit() {
    this.assessmentMethod = this.symptomsAndSignsFormGroup.controls.assessmentMethod.value;
    this.symptomsAndSignsFormGroup.controls.assessmentMethod.valueChanges.subscribe(
      assessmentMethod => {
        this.assessmentMethod = assessmentMethod;
      }
    );
    this.prefetchScribeUsage();
  }

  prefetchScribeUsage() {
    if (!this.clinicId || !this.showAiScribe) {
      return;
    }
    this.clinicSetupService.getAIScribeUsage().subscribe(
      usage => {
        this.cachedScribeUsage = usage;
      },
      () => {}
    );
  }

  refreshScribeUsage() {
    if (!this.clinicId) {
      return;
    }
    this.clinicSetupService.getAIScribeUsage().subscribe(
      usage => {
        this.cachedScribeUsage = usage;
      },
      () => {}
    );
  }

  onScribeRecordingAttachment(s3Object: any) {
    this.pendingScribeAttachment = s3Object;
  }

  onSessionArchived(data: SavedAIScribeData) {
    this.assessmentFormGroup.controls.aiScribeResult.setValue(data);
    if (this.saveFn) {
      this.saveFn().subscribe();
    }
  }

  closeScribe() {
    if (this.isScribeExpanded) {
      this.minimizeScribe();
      return;
    }
    this.isScribeClosing = true;
    timer(200)
      .pipe(take(1))
      .subscribe(() => {
        if (this.isScribeClosing) {
          this.isScribeOpen = false;
          this.isScribeExpanded = false;
          this.isScribeMinimized = false;
          this.isScribeClosing = false;
          this.scribeStep = 'input';
        }
      });
  }

  onPanelAnimationEnd(event: AnimationEvent) {
    if (this.isScribeClosing && event.animationName === 'scribe-panel-out') {
      this.isScribeOpen = false;
      this.isScribeExpanded = false;
      this.isScribeClosing = false;
    }
  }

  openAiScribe() {
    if (this.isScribeOpen && this.isScribeMinimized) {
      this.isScribeMinimized = false;
      return;
    }
    this.isScribeOpen = true;
    this.isScribeMinimized = false;
    if (!(this.assessment && this.assessment.id) && this.saveFn) {
      this.saveFn().subscribe(saved => {
        if (saved && (saved as any).id) {
          this.savedAssessmentId = (saved as any).id;
        }
      });
    }
  }

  minimizeScribe() {
    this.isScribeMinimized = true;
    this.isScribeExpanded = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (
      !this.isScribeOpen ||
      this.isScribeMinimized ||
      this.isScribeExpanded ||
      this.isDialogOpen
    ) {
      return;
    }
    const panel = this.scribePanel && this.scribePanel.nativeElement;
    const fab = document.querySelector('.ai-scribe-fab');
    const target = event.target as Node;

    if (panel && panel.contains(target)) return;
    if (fab && fab.contains(target)) return;

    const overlayContainer = document.querySelector('.cdk-overlay-container');
    if (overlayContainer && overlayContainer.contains(target)) return;

    const assessmentHost = document.querySelector('csi-assessment');
    if (assessmentHost && assessmentHost.contains(target)) return;

    this.minimizeScribe();
  }

  onScribeApplied(output: ApplyOutput) {
    if (this.pendingScribeAttachment) {
      const current = this.assessmentFormGroup.controls.images.value || [];
      this.assessmentFormGroup.controls.images.setValue([...current, this.pendingScribeAttachment]);
      this.pendingScribeAttachment = null;
    }

    let filteredFindings: Record<string, any> = {};
    if (output.findings && Object.keys(output.findings).length > 0) {
      const assessmentMethod = this.symptomsAndSignsFormGroup.controls.assessmentMethod.value;
      const layout = this.layoutsService.allLayoutsById[assessmentMethod];
      const allowedSignKeys: Set<string> = new Set(
        (layout && layout.signsLayout && layout.signsLayout.show) || []
      );
      Object.entries(output.findings).forEach(([key, value]) => {
        const baseKey = key.replace(/^(LEFT_|RIGHT_)/, '');
        if (!allowedSignKeys.size || allowedSignKeys.has(baseKey)) {
          const numValue = Number(value);
          filteredFindings[key] = value !== '' && !isNaN(numValue) ? numValue : value;

          // Method-type signs (NITBUT, LWE, Bulbar Redness, etc.) expose their value
          // control only after a qualitative/quantitative method is selected —
          // MethodSymptomControlComponent registers it lazily (see the
          // *ngIf="evaluationMethod" in method-symptom-control.component.html). When no
          // method is selected yet, pre-register the method (defaulting to qualitative,
          // which is what the extractor emits for these signs) and the value control so
          // patchValue can reach them. If the doctor already chose a method, leave it be.
          const symptom = this.schemaService.symptomMap[baseKey];
          if (symptom && symptom.type === SymptomTypes.Method) {
            const methodKey = symptomMethodKey(key);
            const methodCtrl = this.symptomsAndSignsFormGroup.controls[methodKey];
            if (!methodCtrl || !methodCtrl.value) {
              filteredFindings[methodKey] = 'qualitative';
              if (!methodCtrl) {
                this.symptomsAndSignsFormGroup.addControl(
                  methodKey,
                  new FormControl('qualitative')
                );
              }
              if (!this.symptomsAndSignsFormGroup.controls[key]) {
                this.symptomsAndSignsFormGroup.addControl(key, new FormControl());
              }
            }
          }
        }
      });
    }

    const signConflicts = Object.entries(filteredFindings)
      // Exclude internal method-selector controls (e.g. LEFT_LWE_METHOD) — they are
      // plumbing for method-type signs, paired with their value key which is shown.
      .filter(([key]) => !key.endsWith('_METHOD'))
      .map(([key, incoming]) => {
        const lateralityMatch = key.match(/^(LEFT|RIGHT)_/);
        const laterality = lateralityMatch
          ? (lateralityMatch[1].toLowerCase() as 'left' | 'right')
          : undefined;
        const unit =
          output.findingUnits && output.findingUnits[key] ? output.findingUnits[key] : '';
        const ctrl = this.symptomsAndSignsFormGroup.controls[key];
        const existingRaw = ctrl
          ? String(ctrl.value !== null && ctrl.value !== undefined ? ctrl.value : '')
          : '';
        const incomingStr = String(incoming);
        return {
          label: key.replace(/^(LEFT_|RIGHT_)/, '').replace(/_/g, ' '),
          existing: unit && existingRaw ? existingRaw + ' ' + unit : existingRaw,
          incoming: unit && incomingStr ? incomingStr + ' ' + unit : incomingStr,
          section: 'signs',
          laterality
        };
      })
      .filter(Boolean) as Array<{
      label: string;
      existing: string;
      incoming: string;
      section: string;
      laterality?: 'left' | 'right';
    }>;

    const nonSignConflictKeys: string[] = [];
    const nonSignConflicts: Array<{
      label: string;
      existing: string;
      incoming: string;
      section?: string;
      subSection?: string;
    }> = [];
    const mh = this.medicalHistoryFormGroup.controls as Record<string, any>;
    const h = output.history;
    const a = output.assessment;

    const addTextConflict = (
      key: string,
      label: string,
      incoming: string | null | undefined,
      section: string
    ) => {
      if (!incoming) return;
      const existing = mh[key] ? mh[key].value || '' : '';
      const normalizeForComparison = (s: string) =>
        s
          .replace(/\s+/g, ' ')
          .trim()
          .toLowerCase();
      const normalizeForDisplay = (s: string) => s.replace(/\s+/g, ' ').trim();

      const existingForComparison = normalizeForComparison(String(existing));
      const incomingForComparison = normalizeForComparison(incoming);

      const displayExisting =
        existingForComparison === incomingForComparison
          ? normalizeForDisplay(incoming)
          : normalizeForDisplay(String(existing));

      nonSignConflicts.push({
        label,
        existing: displayExisting,
        incoming: normalizeForDisplay(incoming),
        section
      });
    };

    const pp = output.patientProfile;
    const removedFields = new Set(output.removedFields || []);
    if (pp && this.patient) {
      const addProfileField = (
        label: string,
        incoming: string | null | undefined,
        existing: string | null | undefined,
        removedKey: string,
        section: string = 'patientProfile'
      ) => {
        if (!incoming || removedFields.has(removedKey)) return;
        const normalizeForComparison = (s: string) =>
          s
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase();
        const normalizeForDisplay = (s: string) => s.replace(/\s+/g, ' ').trim();

        const existingForComparison = existing ? normalizeForComparison(String(existing)) : '';
        const incomingForComparison = normalizeForComparison(incoming);

        const displayExisting =
          existingForComparison === incomingForComparison
            ? normalizeForDisplay(incoming)
            : existing
            ? normalizeForDisplay(String(existing))
            : '';

        nonSignConflicts.push({
          label,
          existing: displayExisting,
          incoming: normalizeForDisplay(incoming),
          section
        });
      };

      if (pp.firstName) {
        addProfileField(
          'First Name',
          pp.firstName,
          this.patient.firstName || null,
          'patientProfile.firstName'
        );
      }
      if (pp.lastName) {
        addProfileField(
          'Last Name',
          pp.lastName,
          this.patient.lastName || null,
          'patientProfile.lastName'
        );
      }
      addProfileField('Gender', pp.gender, this.patient.gender, 'patientProfile.gender');
      addProfileField(
        'Date of Birth',
        pp.dateOfBirth,
        this.patient.dateOfBirth,
        'patientProfile.dateOfBirth'
      );
      addProfileField('Email', pp.email, this.patient.email, 'patientProfile.email');
      addProfileField('Phone', pp.phoneNumber, this.patient.phone, 'patientProfile.phone');
      const existingHCNum = this.patient.healthCard && this.patient.healthCard.number;
      addProfileField(
        'Health Card',
        pp.healthCardNumber,
        existingHCNum,
        'patientProfile.healthCard'
      );
      addProfileField('Address', pp.address, this.patient.address, 'patientProfile.address');
      addProfileField('City', pp.city, this.patient.city, 'patientProfile.city');
      addProfileField('Province', pp.province, this.patient.province, 'patientProfile.province');
      addProfileField(
        'Postal Code',
        pp.postalCode,
        this.patient.postalCode,
        'patientProfile.postalCode'
      );
      addProfileField('Country', pp.country, this.patient.country, 'patientProfile.country');
    }

    if (
      h &&
      h.temperatureCelsius !== null &&
      h.temperatureCelsius !== undefined &&
      this.weatherFormGroup
    ) {
      const existing = this.weatherFormGroup.controls.temp.value;
      const incomingNum = h.temperatureCelsius;
      const existingNum =
        existing !== null && existing !== undefined && existing !== ''
          ? parseFloat(String(existing))
          : null;
      nonSignConflicts.push({
        label: 'Weather Temperature',
        existing: existingNum !== null && !isNaN(existingNum) ? existingNum + '°C' : '',
        incoming: incomingNum + '°C',
        section: 'weather'
      });
    }
    if (
      h &&
      h.humidityPercent !== null &&
      h.humidityPercent !== undefined &&
      this.weatherFormGroup
    ) {
      const existing = this.weatherFormGroup.controls.humidity.value;
      const incomingNum = h.humidityPercent;
      const existingNum =
        existing !== null && existing !== undefined && existing !== ''
          ? parseFloat(String(existing))
          : null;
      nonSignConflicts.push({
        label: 'Weather Humidity',
        existing: existingNum !== null && !isNaN(existingNum) ? existingNum + '%' : '',
        incoming: incomingNum + '%',
        section: 'weather'
      });
    }

    if (h) {
      addTextConflict('historyOfPresentingIllness', 'Chief Complaint', h.chiefComplaint, 'history');
      addTextConflict('occupation', 'Occupation', h.occupation, 'history');
      addTextConflict('hobbies', 'Hobbies', h.hobbies, 'history');
      addTextConflict('triggeringEvent', 'Triggering Event', h.triggeringEvent, 'history');
      addTextConflict('changesToEyes', 'Changes to Eyes', h.changesToEyes, 'history');
      addTextConflict(
        'pastDryEyeTreatmentsFreeText',
        'Previous Treatments',
        h.previousTreatments,
        'history'
      );
      addTextConflict(
        'previousOcularSurgeryFreeText',
        'Past Ocular History',
        h.pastOcularHistory,
        'history'
      );
      addTextConflict(
        'faceCareAndMakeUpProductsFreeText',
        'Makeup / Eye Care',
        h.makeupNotes,
        'history'
      );
      addTextConflict('dietFreeText', 'Diet', h.diet, 'history');
      addTextConflict(
        'previousTreatmentIssues',
        'Issues from Previous Treatments',
        h.issuesFromPreviousTreatments,
        'history'
      );
      addTextConflict(
        'otherConditionsFreeText',
        'Contact Lens Duration / Type',
        h.contactLensDurationType,
        'history'
      );

      if (h.sleepPerNight !== null && h.sleepPerNight !== undefined && mh['sleepPerNight']) {
        const existing = mh['sleepPerNight'].value;
        const incomingStr = String(h.sleepPerNight) + ' hrs';
        nonSignConflicts.push({
          label: 'Sleep Per Night',
          existing:
            existing !== null && existing !== undefined && existing !== ''
              ? String(existing) + ' hrs'
              : '',
          incoming: incomingStr,
          section: 'history'
        });
      }

      const lifestyleSliders: Array<{
        key: string;
        label: string;
        incoming: number | null | undefined;
      }> = [
        { key: 'dietQuality', label: 'Diet Quality', incoming: h.dietQuality },
        { key: 'healthQuality', label: 'Health Quality', incoming: h.healthQuality },
        { key: 'stressLevel', label: 'Stress Level', incoming: h.stressLevel },
        { key: 'cityHumidityLevel', label: 'City Humidity', incoming: h.cityHumidityLevel },
        { key: 'cityPollutionLevel', label: 'City Pollution', incoming: h.cityPollutionLevel },
        { key: 'sensitivityToLight', label: 'Light Sensitivity', incoming: h.sensitivityToLight },
        { key: 'sensitivityToWind', label: 'Wind Sensitivity', incoming: h.sensitivityToWind },
        { key: 'impactOfSymptomsOnMood', label: 'Impact on Mood', incoming: h.impactOnMood },
        {
          key: 'impactOfSymptomsOnLife',
          label: 'Impact on Daily Life',
          incoming: h.impactOnDailyLife
        }
      ];
      lifestyleSliders.forEach(field => {
        if (field.incoming !== null && field.incoming !== undefined && mh[field.key]) {
          const existing = mh[field.key].value;
          const incomingStr = String(field.incoming) + '/10';
          nonSignConflicts.push({
            label: field.label,
            existing:
              existing !== null && existing !== undefined && existing !== 0
                ? String(existing / 10) + '/10'
                : '',
            incoming: incomingStr,
            section: 'history'
          });
        }
      });

      if (h.workHoursPerDay !== null && h.workHoursPerDay !== undefined)
        addTextConflict(
          'workHoursPerDayFreeText',
          'Work Hrs/Day',
          String(h.workHoursPerDay),
          'history'
        );
      if (h.exerciseHoursPerWeek !== null && h.exerciseHoursPerWeek !== undefined)
        addTextConflict('exercise', 'Exercise Hrs/Week', String(h.exerciseHoursPerWeek), 'history');
      if (h.waterPerDay !== null && h.waterPerDay !== undefined)
        addTextConflict('waterPerDayFreeText', 'Water/Day', String(h.waterPerDay), 'history');
      if (h.caffeinatedDrinksPerDay !== null && h.caffeinatedDrinksPerDay !== undefined)
        addTextConflict(
          'caffeinatedDrinksPerDayFreeText',
          'Caffeinated Drinks/Day',
          String(h.caffeinatedDrinksPerDay),
          'history'
        );
      if (h.timeSpentOutdoors !== null && h.timeSpentOutdoors !== undefined)
        addTextConflict(
          'timeSpentOutdoorsFreeText',
          'Time Outdoors',
          String(h.timeSpentOutdoors),
          'history'
        );
      if (h.timeSpentClosedEnvironments !== null && h.timeSpentClosedEnvironments !== undefined)
        addTextConflict(
          'timeSpentClosedEnvironmentsFreeText',
          'Closed Environments',
          String(h.timeSpentClosedEnvironments),
          'history'
        );
      if (h.urbanAreaTimePercent !== null && h.urbanAreaTimePercent !== undefined)
        addTextConflict(
          'urbanAreaTimePerWeekFreeText',
          'Time in Urban Areas',
          String(h.urbanAreaTimePercent),
          'history'
        );
    }

    if (h) {
      if (h.medications) {
        const medsArray: any[] = mh['medications'] ? mh['medications'].value || [] : [];
        const medsStr = Array.isArray(medsArray)
          ? medsArray.map((m: any) => (m && m.name ? m.name : String(m))).join(', ')
          : String(medsArray || '');
        const normalize = (s: string) =>
          s
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase();

        const aiMedNames = h.medications
          .split(/[,;]+/)
          .map(n => normalize(n))
          .filter(n => n.length > 0);

        const existingMedNames = medsStr
          .split(/[,;]+/)
          .map(n => normalize(n))
          .filter(n => n.length > 0);

        const allAiMedsExist = aiMedNames.every(aiMed =>
          existingMedNames.some(
            existingMed => existingMed.includes(aiMed) || aiMed.includes(existingMed)
          )
        );

        nonSignConflicts.push({
          label: 'Medications',
          existing: medsStr.trim(),
          incoming: h.medications.trim(),
          section: 'medications'
        });
      }
      if (h.currentOcularMedications) {
        const existingOcular = mh['currentOcularMedicationsFreeText']
          ? String(mh['currentOcularMedicationsFreeText'].value || '').trim()
          : '';
        nonSignConflicts.push({
          label: 'Current Ocular Meds',
          existing: existingOcular,
          incoming: h.currentOcularMedications.trim(),
          section: 'medications'
        });
      }
      if (h.pastOcularMedications) {
        addTextConflict(
          'pastOcularMedicationsFreeText',
          'Past Ocular Meds',
          h.pastOcularMedications,
          'medications'
        );
      }
      if (h.nutraceuticals) {
        const existingNutra = mh['otherMedications']
          ? String(mh['otherMedications'].value || '').trim()
          : '';
        nonSignConflicts.push({
          label: 'Nutraceuticals',
          existing: existingNutra,
          incoming: h.nutraceuticals.trim(),
          section: 'medications'
        });
      }
      if (h.drugAllergies) {
        addTextConflict('drugAllergies', 'Drug Allergies', h.drugAllergies, 'history');
      }
      if (h.seasonalAllergies) {
        addTextConflict('seasonalAllergies', 'Seasonal Allergies', h.seasonalAllergies, 'history');
      }
      if (h.otherAllergies) {
        addTextConflict('otherAllergies', 'Other Allergies', h.otherAllergies, 'history');
      }
      if (h.lastEyeExam) {
        nonSignConflicts.push({
          label: 'Last Eye Exam',
          existing: '',
          incoming: h.lastEyeExam,
          section: 'history'
        });
      }
      if (h.symptomDuration) {
        nonSignConflicts.push({
          label: 'Symptom Duration',
          existing: '',
          incoming: h.symptomDuration,
          section: 'history'
        });
      }
      if (h.conditions && h.conditions.length > 0) {
        const existingConditions = h.conditions
          .filter(key => mh[key] && mh[key].value === true)
          .map(key => key);
        const existingDisplay = existingConditions.length > 0 ? existingConditions.join(', ') : '';
        nonSignConflicts.push({
          label: 'Conditions',
          existing: existingDisplay,
          incoming: h.conditions.join(', '),
          section: 'history'
        });
      }
      if (h.negatedConditions && h.negatedConditions.length > 0) {
        const existingNegated = h.negatedConditions
          .filter(key => mh[key] && mh[key].value === false)
          .map(key => key);
        const existingNegatedDisplay = existingNegated.length > 0 ? existingNegated.join(', ') : '';
        nonSignConflicts.push({
          label: 'Negated Conditions',
          existing: existingNegatedDisplay,
          incoming: h.negatedConditions.join(', '),
          section: 'history'
        });
      }
      if (h.environmentalSymptoms && h.environmentalSymptoms.length > 0) {
        const existingEnvSymptoms = h.environmentalSymptoms
          .filter(key => mh[key] && mh[key].value === true)
          .map(key => key);
        const existingEnvDisplay =
          existingEnvSymptoms.length > 0 ? existingEnvSymptoms.join(', ') : '';
        nonSignConflicts.push({
          label: 'Environmental Symptoms',
          existing: existingEnvDisplay,
          incoming: h.environmentalSymptoms.join(', '),
          section: 'history'
        });
      }
    }

    const s = output.symptoms;
    const symptomSliders: Array<{ mhKey: string; label: string; value: string | null }> = [
      { mhKey: 'eyeItchiness', label: 'Eye Itchiness', value: s ? s.itchiness : null },
      { mhKey: 'eyeTearingQuantity', label: 'Tearing Quantity', value: s ? s.tearing : null }
    ];
    const severityToSliderPreview: Record<string, number> = {
      none: 0,
      mild: 30,
      moderate: 50,
      severe: 70
    };
    symptomSliders.forEach(field => {
      if (field.value && mh[field.mhKey]) {
        const existing = mh[field.mhKey].value;
        const incomingNumeric =
          severityToSliderPreview[field.value] !== undefined
            ? severityToSliderPreview[field.value]
            : 0;
        nonSignConflicts.push({
          label: field.label,
          existing:
            existing !== null && existing !== undefined && existing !== 0 ? String(existing) : '',
          incoming: field.value,
          section: 'history'
        });
      }
    });

    const symptomRadios: Array<{ mhKey: string; label: string; value: string | null }> = [
      { mhKey: 'tearingQuality', label: 'Tearing Quality', value: s ? s.tearingQuality : null },
      {
        mhKey: 'sleepingPosition',
        label: 'Sleeping Position',
        value: s ? s.sleepingPosition : null
      },
      {
        mhKey: 'eyeRubbingFrequency',
        label: 'Eye Rubbing',
        value: s ? s.eyeRubbingFrequency : null
      },
      { mhKey: 'bothersomeEye', label: 'Bothersome Eye', value: s ? s.bothersomeEye : null },
      { mhKey: 'screenTime', label: 'Screen Time', value: s ? s.screenTime : null },
      {
        mhKey: 'symptomSeverityOccurrence',
        label: 'Symptoms Worst At',
        value: s ? s.symptomSeverityOccurrence : null
      }
    ];
    symptomRadios.forEach(field => {
      if (field.value && mh[field.mhKey]) {
        const existing = mh[field.mhKey].value;
        nonSignConflicts.push({
          label: field.label,
          existing: existing ? String(existing) : '',
          incoming: field.value,
          section: 'history'
        });
      }
    });

    const scoreConflicts: Array<{
      formKey: string;
      label: string;
      incoming: number | null | undefined;
    }> = [];
    if (s && s.osdiScore !== null && s.osdiScore !== undefined)
      scoreConflicts.push({ formKey: 'osdi', label: 'OSDI Score', incoming: s.osdiScore });
    if (s && s.speedScore !== null && s.speedScore !== undefined)
      scoreConflicts.push({ formKey: 'speed', label: 'SPEED Score', incoming: s.speedScore });
    if (s && s.speedIIScore !== null && s.speedIIScore !== undefined)
      scoreConflicts.push({
        formKey: 'speedii',
        label: 'SPEED II Score',
        incoming: s.speedIIScore
      });
    if (s && s.osdi6Score !== null && s.osdi6Score !== undefined)
      scoreConflicts.push({ formKey: 'osdi6', label: 'OSDI-6 Score', incoming: s.osdi6Score });
    if (s && s.deqScore !== null && s.deqScore !== undefined)
      scoreConflicts.push({ formKey: 'deq', label: 'DEQ Score', incoming: s.deqScore });
    if (s && s.fitzpatrickScore !== null && s.fitzpatrickScore !== undefined)
      scoreConflicts.push({
        formKey: 'fitzpatrick',
        label: 'Fitzpatrick Type',
        incoming: s.fitzpatrickScore
      });
    scoreConflicts.forEach(field => {
      const ctrl = this.symptomsAndSignsFormGroup.controls[field.formKey];
      if (ctrl) {
        const existing = ctrl.value;
        nonSignConflicts.push({
          label: field.label,
          existing:
            existing !== null && existing !== undefined && existing !== '' ? String(existing) : '',
          incoming: String(field.incoming),
          section: 'symptoms'
        });
      }
    });

    const pf = output.patientFile;
    if (pf) {
      const existingNonMod: Record<string, unknown> =
        this.patient && this.patient.nonModifiableData
          ? JSON.parse(this.patient.nonModifiableData)
          : {};
      const patientFileFields: Array<{ key: string; label: string; value: string | null }> = [
        { key: 'wearsGlasses', label: 'Wears Glasses', value: pf.wearsGlasses },
        { key: 'eyeColor', label: 'Eye Color', value: pf.eyeColor },
        { key: 'skinColor', label: 'Skin Color', value: pf.skinColor },
        { key: 'naturalHairColor', label: 'Hair Color', value: pf.naturalHairColor },
        { key: 'freckles', label: 'Freckles', value: pf.freckles },
        { key: 'tanIntensity', label: 'Tan Intensity', value: pf.tanIntensity },
        { key: 'faceSunSensitivity', label: 'Sun Sensitivity', value: pf.faceSunSensitivity },
        { key: 'longSunExposure', label: 'Long Sun Reaction', value: pf.longSunExposure },
        { key: 'darkerAfterSunExposure', label: 'Tan After Sun', value: pf.darkerAfterSunExposure },
        {
          key: 'sunTanExposureFrequency',
          label: 'Sun Exposure Freq.',
          value: pf.sunTanExposureFrequency
        },
        { key: 'lastSunExposure', label: 'Last Sun Exposure', value: pf.lastSunExposure },
        { key: 'ethnicity', label: 'Ethnicity', value: pf.ethnicity },
        { key: 'education', label: 'Education Level', value: pf.education },
        {
          key: 'eyelashModification',
          label: 'Eyelash Modification',
          value: pf.eyelashModification
        },
        {
          key: 'sickBuildingSyndrome',
          label: 'Sick Building Syndrome',
          value: pf.sickBuildingSyndrome
        },
        { key: 'styeOrChalazion', label: 'Stye / Chalazion', value: pf.styeOrChalazion }
      ];
      patientFileFields.forEach(field => {
        if (!field.value) return;
        const existingVal =
          existingNonMod[field.key] || (mh[field.key] ? mh[field.key].value : null);
        const normalizeForDisplay = (s: string) => s.replace(/\s+/g, ' ').trim();
        nonSignConflicts.push({
          label: field.label,
          existing: existingVal ? normalizeForDisplay(String(existingVal)) : '',
          incoming: normalizeForDisplay(field.value),
          section: 'patientProfile',
          subSection: 'patientFile'
        });
      });
      if (pf.familyHistory) {
        const existingFH = existingNonMod['familyHistory'];
        nonSignConflicts.push({
          label: 'Family History',
          existing: existingFH ? String(existingFH) : '',
          incoming: pf.familyHistory,
          section: 'patientProfile',
          subSection: 'patientFile'
        });
      }
      if (pf.childBirth) {
        const existingCB = existingNonMod['childBirth'];
        nonSignConflicts.push({
          label: 'Child Birth',
          existing: existingCB ? String(existingCB) : '',
          incoming: pf.childBirth,
          section: 'patientProfile',
          subSection: 'patientFile'
        });
      }
      if (pf.gender && pf.gender.length > 0) {
        const genderStr = pf.gender.join(', ');
        const existingGender = existingNonMod['gender'];
        const existingGenderStr = existingGender
          ? Array.isArray(existingGender)
            ? existingGender.join(', ')
            : String(existingGender)
          : '';
        nonSignConflicts.push({
          label: 'Gender Identity',
          existing: existingGenderStr,
          incoming: genderStr,
          section: 'patientProfile',
          subSection: 'patientFile'
        });
      }
    }

    if (a) {
      if (this.assessmentFormGroup.controls.category) {
        const cat = this.assessmentFormGroup.controls.category.value || {};
        if (a.dryEyeType) {
          nonSignConflicts.push({
            label: 'Dry Eye Type',
            existing: cat.type || '',
            incoming: a.dryEyeType,
            section: 'assessment'
          });
        }
        if (a.dryEyeSeverity) {
          nonSignConflicts.push({
            label: 'Dry Eye Severity',
            existing: cat.severity || '',
            incoming: a.dryEyeSeverity,
            section: 'assessment'
          });
        }
      }
      if (a.impressions) {
        const existingImpressions = this.stripHtml(
          (this.assessmentFormGroup.controls.impressions.value || '').toString()
        );
        const incomingImpressions = this.toTitleCase(a.impressions).trim();
        nonSignConflicts.push({
          label: 'Impressions',
          existing: existingImpressions,
          incoming: incomingImpressions,
          section: 'assessment'
        });
      }
      if (a.treatmentPerformed) {
        const existingTreatment = this.assessmentFormGroup.controls.treatmentPerformed
          ? (this.assessmentFormGroup.controls.treatmentPerformed.value || '').toString().trim()
          : '';
        const incomingTreatment = a.treatmentPerformed.trim();
        nonSignConflicts.push({
          label: 'Treatment Performed',
          existing: existingTreatment,
          incoming: incomingTreatment,
          section: 'assessment'
        });
      }
      if (a.recommendedPlan) {
        const existingPlan = (this.assessmentFormGroup.controls.recommendedPlanByDoctor.value || '')
          .toString()
          .trim();
        const incomingPlan = this.toTitleCase(a.recommendedPlan).trim();
        nonSignConflicts.push({
          label: 'Recommended Plan',
          existing: existingPlan,
          incoming: incomingPlan,
          section: 'assessment'
        });
      }
      if (a.recommendedTreatments) {
        const rt = a.recommendedTreatments;

        const existingTreatments: Treatment[] = [
          ...((this.assessmentFormGroup.controls.recommendedEyeDrops.value as Treatment[]) || []),
          ...((this.assessmentFormGroup.controls.recommendedRxEyeDrops.value as Treatment[]) || []),
          ...((this.assessmentFormGroup.controls.recommendedProcedures.value as Treatment[]) || []),
          ...((this.assessmentFormGroup.controls.recommendedExercises.value as Treatment[]) || [])
        ];
        const existingIds = new Set(existingTreatments.map(t => t.id));
        const existingNames = existingTreatments.map(t => t.name);

        const allAiNames: string[] = [
          ...(rt.otc || []),
          ...(rt.rx || []),
          ...(rt.procedures || []),
          ...(rt.exercises || [])
        ];

        nonSignConflicts.push({
          label: 'Recommended Treatments',
          existing: existingNames.length > 0 ? existingNames.join(', ') : '',
          incoming: allAiNames.join(', '),
          section: 'assessment'
        });
      }
      if (a.goalsForPatient) {
        const existingGoals = (this.assessmentFormGroup.controls.goalsForPatient.value || '')
          .toString()
          .trim();
        const incomingGoals = this.toTitleCase(a.goalsForPatient).trim();
        nonSignConflicts.push({
          label: 'Goals for Patient',
          existing: existingGoals,
          incoming: incomingGoals,
          section: 'assessment'
        });
      }
    }

    // DEWS III differential diagnosis screening conflicts
    const ds = output.dewsScreening;
    if (ds) {
      const dewsGroup = this.symptomsAndSignsFormGroup.controls[
        'dewsIIIDifferentialDiagnosisScreening'
      ] as any;
      const dewsControls = dewsGroup && dewsGroup.controls ? dewsGroup.controls : null;

      const addDewsRadio = (
        field: string,
        label: string,
        incoming: boolean | null | undefined
      ) => {
        if (incoming === null || incoming === undefined) { return; }
        const existingVal = dewsControls && dewsControls[field] ? dewsControls[field].value : null;
        const incomingStr = incoming ? 'Yes' : 'No';
        const existingStr = existingVal === 'yes' ? 'Yes' : existingVal === 'no' ? 'No' : '';
        nonSignConflicts.push({
          label: label,
          existing: existingStr,
          incoming: incomingStr,
          section: 'dewsScreening'
        });
      };

      const addDewsCheckbox = (
        field: string,
        label: string,
        incoming: boolean | null | undefined
      ) => {
        if (incoming === null || incoming === undefined) { return; }
        const existingVal = dewsControls && dewsControls[field] ? dewsControls[field].value : null;
        const incomingStr = incoming ? 'Present' : 'Absent';
        const existingStr = existingVal === true ? 'Present' : existingVal === false ? 'Absent' : '';
        nonSignConflicts.push({
          label: label,
          existing: existingStr,
          incoming: incomingStr,
          section: 'dewsScreening'
        });
      };

      addDewsRadio('suddenOnset', 'Was the onset sudden?', ds.suddenOnset);
      addDewsRadio('visionAffected', 'Is vision affected?', ds.visionAffected);
      addDewsRadio('improvedByBlinking', 'Symptoms improved by blinking?', ds.improvedByBlinking);
      addDewsRadio('frankPain', 'Frank pain (rather than discomfort)?', ds.frankPain);
      addDewsRadio('unilateral', 'Is presentation unilateral?', ds.unilateral);
      addDewsCheckbox('facialFlushing', 'Facial flushing', ds.facialFlushing);
      addDewsCheckbox('mouthDryness', 'Mouth dryness', ds.mouthDryness);
      addDewsCheckbox('enlargedSalivaryGlands', 'Enlarged salivary glands', ds.enlargedSalivaryGlands);
      addDewsCheckbox('itching', 'Itching', ds.itching);
      addDewsCheckbox('eyesSwollen', 'Swollen eyelids', ds.eyesSwollen);
      addDewsCheckbox('crustyLashes', 'Crusty lashes', ds.crustyLashes);
      addDewsCheckbox('mucopurulentDischarge', 'Mucopurulent discharge', ds.mucopurulentDischarge);
    }

    const allConflicts = [...signConflicts, ...nonSignConflicts];

    if (allConflicts.length > 0) {
      const cdkOverlay = document.querySelector('.cdk-overlay-container') as HTMLElement;
      if (cdkOverlay) {
        cdkOverlay.style.zIndex = '1100';
      }
      this.isDialogOpen = true;
      const dialogRef = this.dialog.open(ScribeOverwriteDialogComponent, {
        data: { fields: allConflicts, additions: [] },
        width: '720px'
      });
      dialogRef.afterClosed().subscribe((choice: OverwriteChoice) => {
        const overlay = document.querySelector('.cdk-overlay-container') as HTMLElement;
        if (overlay) {
          overlay.style.zIndex = '';
        }
        setTimeout(() => {
          this.isDialogOpen = false;
        }, 0);
        if (!choice || choice === 'cancel') {
          return;
        }
        const skipExisting = choice === 'new-only';
        const overwriteAll = choice === 'all';

        const toApply = skipExisting
          ? Object.entries(filteredFindings)
              .filter(([key]) => {
                const ctrl = this.symptomsAndSignsFormGroup.controls[key];
                const v = ctrl ? ctrl.value : null;
                return v === null || v === undefined || v === '';
              })
              .reduce((acc, [k, v]) => {
                acc[k] = v;
                return acc;
              }, {} as { [key: string]: unknown })
          : filteredFindings;

        if (Object.keys(toApply).length > 0) {
          this.symptomsAndSignsFormGroup.patchValue(toApply);
        }
        this.finishScribeApply(output, skipExisting ? nonSignConflictKeys : [], overwriteAll);
      });
      return;
    }

    if (Object.keys(filteredFindings).length > 0) {
      this.symptomsAndSignsFormGroup.patchValue(filteredFindings);
    }
    this.finishScribeApply(output, [], false);
  }

  private finishScribeApply(
    output: ApplyOutput,
    skipFieldKeys: string[],
    overwriteAll: boolean = false
  ) {
    const skip = new Set(skipFieldKeys);
    const removed = new Set(output.removedFields || []);
    const mh = this.medicalHistoryFormGroup.controls as Record<string, any>;

    if (
      !removed.has('assessment.impressions') &&
      output.impressions &&
      this.assessmentFormGroup.controls.impressions
    ) {
      const text = this.toTitleCase(output.impressions);
      const existing = (this.assessmentFormGroup.controls.impressions.value || '')
        .toString()
        .trim();
      this.assessmentFormGroup.controls.impressions.setValue(
        existing && !existing.includes(text) ? existing + '\n' + text : existing || text
      );
    }
    if (
      !removed.has('assessment.treatmentPerformed') &&
      output.treatmentPlan &&
      this.assessmentFormGroup.controls.treatmentPerformed
    ) {
      const text = this.toTitleCase(output.treatmentPlan);
      const existing = (this.assessmentFormGroup.controls.treatmentPerformed.value || '')
        .toString()
        .trim();
      this.assessmentFormGroup.controls.treatmentPerformed.setValue(
        existing && !existing.includes(text) ? existing + '\n' + text : existing || text
      );
    }

    const a = output.assessment;
    if (a) {
      if (a.recommendedTreatments && this.treatmentsService.idToTreatmentMap.size > 0) {
        const rt = a.recommendedTreatments;
        const byClass = (cls: string): Treatment[] =>
          Array.from(this.treatmentsService.idToTreatmentMap.values()).filter(t => t.class === cls);

        const matchList = (names: string[], cls: string): Treatment[] =>
          (names || []).reduce((acc: Treatment[], name) => {
            const match = this.matchTreatmentByName(name, byClass(cls));
            if (match && !acc.find(t => t.id === match.id)) {
              acc.push(match);
            }
            return acc;
          }, []);

        this.mergeIntoTreatmentControl(
          this.assessmentFormGroup.controls.recommendedEyeDrops,
          matchList(rt.otc, 'otc')
        );
        this.mergeIntoTreatmentControl(
          this.assessmentFormGroup.controls.recommendedRxEyeDrops,
          matchList(rt.rx, 'rx')
        );
        this.mergeIntoTreatmentControl(
          this.assessmentFormGroup.controls.recommendedProcedures,
          matchList(rt.procedures, 'procedure')
        );
        this.mergeIntoTreatmentControl(
          this.assessmentFormGroup.controls.recommendedExercises,
          matchList(rt.exercises, 'exercise')
        );
      }

      if (
        !removed.has('assessment.classification') &&
        (a.dryEyeType || a.dryEyeSeverity) &&
        this.assessmentFormGroup.controls.category
      ) {
        const current = this.assessmentFormGroup.controls.category.value;
        const existingType = current && current.type ? current.type : null;
        const existingSeverity = current && current.severity ? current.severity : null;
        const newType = !skip.has('dryEyeType') && a.dryEyeType ? a.dryEyeType : existingType;
        const newSeverity =
          !skip.has('dryEyeSeverity') && a.dryEyeSeverity ? a.dryEyeSeverity : existingSeverity;
        if (newType && newSeverity) {
          this.assessmentFormGroup.controls.category.setValue({
            type: newType,
            severity: newSeverity
          });
        }
      }
      if (
        !removed.has('assessment.recommendedPlan') &&
        a.recommendedPlan &&
        this.assessmentFormGroup.controls.recommendedPlanByDoctor
      ) {
        const text = this.toTitleCase(a.recommendedPlan);
        const existing = (this.assessmentFormGroup.controls.recommendedPlanByDoctor.value || '')
          .toString()
          .trim();
        if (!existing.includes(text)) {
          this.assessmentFormGroup.controls.recommendedPlanByDoctor.setValue(
            existing ? existing + '\n' + text : text
          );
        }
      }
      if (
        !removed.has('assessment.goals') &&
        a.goalsForPatient &&
        this.assessmentFormGroup.controls.goalsForPatient
      ) {
        const text = this.toTitleCase(a.goalsForPatient);
        const existing = (this.assessmentFormGroup.controls.goalsForPatient.value || '')
          .toString()
          .trim();
        if (!existing.includes(text)) {
          this.assessmentFormGroup.controls.goalsForPatient.setValue(
            existing ? existing + '\n' + text : text
          );
        }
      }
    }

    const h = output.history;
    if (h) {
      const appendField = (key: string, value: string) => {
        if (!mh[key]) {
          return;
        }
        const existing = (mh[key].value || '').toString().trim();
        mh[key].setValue(existing ? existing + '\n' + value : value);
      };
      // Matches negative answers Gemini may return for a yes/no allergy question
      // (e.g. "None", "No", "N/A", "Nil", "Denied") — these must map to the literal
      // 'none' sentinel, not be written as free text, or csi-toggleable-medical-history-text-field
      // will show "Yes" selected with that word as the detail text.
      const NEGATIVE_ANSWER_RE = /^(none|no|n\/a|nil|denied|negative)\.?$/i;
      const setToggleField = (key: string, value: string) => {
        if (!mh[key]) {
          return;
        }
        const existing = mh[key].value;
        if (!existing || existing === 'none') {
          mh[key].setValue(NEGATIVE_ANSWER_RE.test(value.trim()) ? 'none' : value);
        }
      };
      if (!removed.has('history.chiefComplaint') && h.chiefComplaint) {
        appendField('historyOfPresentingIllness', this.toTitleCase(h.chiefComplaint));
      }
      if (!removed.has('history.medications') && h.medications) {
        appendField('patientEnteredMedicationsFreeText', this.toTitleCase(h.medications));
        this.applyMedicationsFromScribe(h.medications);
      }
      if (!removed.has('history.currentOcularMeds') && h.currentOcularMedications) {
        this.applyMedicationsFromScribe(
          h.currentOcularMedications,
          'currentOcularMedicationsFreeText'
        );
      }
      if (!removed.has('history.nutraceuticals') && h.nutraceuticals) {
        appendField('otherMedications', this.toTitleCase(h.nutraceuticals));
      }
      if (!removed.has('history.lastEyeExam') && h.lastEyeExam && mh['lastEyeExam']) {
        const eyeExamDate = this.parseTextToDate(h.lastEyeExam);
        if (eyeExamDate) {
          // closeDatePicker stores the value as "YYYY/MM" — match that format exactly
          // so the plain matInput displays it correctly instead of "[object Object]"
          const year = eyeExamDate.getFullYear();
          const month = eyeExamDate.getMonth() + 1;
          const monthStr = month < 10 ? '0' + month : '' + month;
          mh['lastEyeExam'].setValue(year + '/' + monthStr);
        }
      }
      if (!removed.has('history.changesToEyes') && h.changesToEyes) {
        appendField('changesToEyes', this.toTitleCase(h.changesToEyes));
      }
      if (!removed.has('history.symptomDuration') && h.symptomDuration) {
        const parsed = this.parseSymptomDuration(h.symptomDuration);
        if (parsed && mh['symptomDuration']) {
          mh['symptomDuration'].setValue(parsed);
        }
      }
      if (!removed.has('history.drugAllergies') && h.drugAllergies) {
        setToggleField('drugAllergies', this.toTitleCase(h.drugAllergies));
      }
      if (!removed.has('history.seasonalAllergies') && h.seasonalAllergies) {
        setToggleField('seasonalAllergies', this.toTitleCase(h.seasonalAllergies));
      }
      if (!removed.has('history.otherAllergies') && h.otherAllergies) {
        setToggleField('otherAllergies', this.toTitleCase(h.otherAllergies));
      }
      if (!removed.has('history.occupation') && h.occupation) {
        appendField('occupation', this.toTitleCase(h.occupation));
      }
      if (!removed.has('history.hobbies') && h.hobbies) {
        appendField('hobbies', this.toTitleCase(h.hobbies));
      }
      if (!removed.has('history.triggeringEvent') && h.triggeringEvent) {
        appendField('triggeringEvent', this.toTitleCase(h.triggeringEvent));
      }
      if (!removed.has('history.previousTreatments') && h.previousTreatments) {
        appendField('pastDryEyeTreatmentsFreeText', this.toTitleCase(h.previousTreatments));
      }
      if (!removed.has('history.pastOcularHistory') && h.pastOcularHistory) {
        appendField('previousOcularSurgeryFreeText', this.toTitleCase(h.pastOcularHistory));
      }
      if (!removed.has('history.pastOcularMedications') && h.pastOcularMedications) {
        this.applyMedicationsFromScribe(h.pastOcularMedications, 'pastOcularMedicationsFreeText');
      }
      if (!removed.has('history.contactLensDurationType') && h.contactLensDurationType) {
        appendField('otherConditionsFreeText', this.toTitleCase(h.contactLensDurationType));
      }
      if (!removed.has('history.issuesFromPreviousTreatments') && h.issuesFromPreviousTreatments) {
        appendField('previousTreatmentIssues', this.toTitleCase(h.issuesFromPreviousTreatments));
      }
      if (
        !removed.has('history.urbanAreaTimePercent') &&
        h.urbanAreaTimePercent !== null &&
        h.urbanAreaTimePercent !== undefined &&
        mh['urbanAreaTimePerWeekFreeText']
      ) {
        mh['urbanAreaTimePerWeekFreeText'].setValue(String(h.urbanAreaTimePercent));
      }
      if (
        !removed.has('history.caffeinatedDrinksDay') &&
        h.caffeinatedDrinksPerDay !== null &&
        h.caffeinatedDrinksPerDay !== undefined &&
        mh['caffeinatedDrinksPerDayFreeText']
      ) {
        mh['caffeinatedDrinksPerDayFreeText'].setValue(String(h.caffeinatedDrinksPerDay));
      }
      if (
        !removed.has('history.waterDay') &&
        h.waterPerDay !== null &&
        h.waterPerDay !== undefined &&
        mh['waterPerDayFreeText']
      ) {
        mh['waterPerDayFreeText'].setValue(String(h.waterPerDay));
      }
      if (
        !removed.has('history.timeOutdoors') &&
        h.timeSpentOutdoors !== null &&
        h.timeSpentOutdoors !== undefined &&
        mh['timeSpentOutdoorsFreeText']
      ) {
        mh['timeSpentOutdoorsFreeText'].setValue(String(h.timeSpentOutdoors));
      }
      if (
        !removed.has('history.closedEnvironments') &&
        h.timeSpentClosedEnvironments !== null &&
        h.timeSpentClosedEnvironments !== undefined &&
        mh['timeSpentClosedEnvironmentsFreeText']
      ) {
        mh['timeSpentClosedEnvironmentsFreeText'].setValue(String(h.timeSpentClosedEnvironments));
      }
      if (!removed.has('history.makeupNotes') && h.makeupNotes) {
        appendField('faceCareAndMakeUpProductsFreeText', h.makeupNotes);
      }
      if (!removed.has('history.diet') && h.diet) {
        appendField('dietFreeText', this.toTitleCase(h.diet));
      }
      if (
        !removed.has('history.sleepPerNight') &&
        !skip.has('sleepPerNight') &&
        h.sleepPerNight !== null &&
        h.sleepPerNight !== undefined &&
        mh['sleepPerNight']
      ) {
        mh['sleepPerNight'].setValue(h.sleepPerNight);
      }

      const setSlider10 = (key: string, val: number | null | undefined, removedKey: string) => {
        if (
          val !== null &&
          val !== undefined &&
          mh[key] &&
          !skip.has(key) &&
          !removed.has(removedKey)
        ) {
          mh[key].setValue(val * 10);
        }
      };
      setSlider10('dietQuality', h.dietQuality, 'history.dietQuality');
      setSlider10('healthQuality', h.healthQuality, 'history.healthQuality');
      setSlider10('stressLevel', h.stressLevel, 'history.stressLevel');
      setSlider10('cityHumidityLevel', h.cityHumidityLevel, 'history.cityHumidity');
      setSlider10('cityPollutionLevel', h.cityPollutionLevel, 'history.cityPollution');
      setSlider10('sensitivityToLight', h.sensitivityToLight, 'history.lightSensitivity');
      setSlider10('sensitivityToWind', h.sensitivityToWind, 'history.windSensitivity');
      setSlider10('impactOfSymptomsOnMood', h.impactOnMood, 'history.impactMood');
      setSlider10('impactOfSymptomsOnLife', h.impactOnDailyLife, 'history.impactDailyLife');

      if (
        !removed.has('history.workHrsDay') &&
        h.workHoursPerDay !== null &&
        h.workHoursPerDay !== undefined &&
        mh['workHoursPerDayFreeText']
      ) {
        mh['workHoursPerDayFreeText'].setValue(String(h.workHoursPerDay));
      }
      if (
        !removed.has('history.exerciseHrsWeek') &&
        h.exerciseHoursPerWeek !== null &&
        h.exerciseHoursPerWeek !== undefined &&
        mh['exercise']
      ) {
        mh['exercise'].setValue(h.exerciseHoursPerWeek);
      }

      if (
        !removed.has('weather.humidity') &&
        !skip.has('weatherHumidity') &&
        h.humidityPercent !== null &&
        h.humidityPercent !== undefined &&
        this.weatherFormGroup
      ) {
        this.weatherFormGroup.controls.humidity.setValue(h.humidityPercent);
      }
      if (
        !removed.has('weather.temperature') &&
        !skip.has('weatherTemp') &&
        h.temperatureCelsius !== null &&
        h.temperatureCelsius !== undefined &&
        this.weatherFormGroup
      ) {
        this.weatherFormGroup.controls.tempUnit.setValue(TemperatureUnit.celsius);
        this.weatherFormGroup.controls.temp.setValue(h.temperatureCelsius);
      }

      if (
        !removed.has('history.environmentalSymptoms') &&
        h.environmentalSymptoms &&
        h.environmentalSymptoms.length > 0
      ) {
        const envPatch: Record<string, boolean> = {};
        h.environmentalSymptoms.forEach(key => {
          if (mh[key] !== undefined) {
            envPatch[key] = true;
          }
        });
        if (Object.keys(envPatch).length > 0) {
          this.medicalHistoryFormGroup.patchValue(envPatch);
        }
      }

      if (!removed.has('history.conditions') && h.conditions && h.conditions.length > 0) {
        const conditionPatch: Record<string, boolean> = {};
        h.conditions.forEach(key => {
          if (mh[key] !== undefined) {
            conditionPatch[key] = true;
          }
        });
        if (Object.keys(conditionPatch).length > 0) {
          this.medicalHistoryFormGroup.patchValue(conditionPatch);
        }

        if (h.conditions.includes('autoimmune') && mh['autoImmuneConditions']) {
          const existing = mh['autoImmuneConditions'].value;
          if (!existing || existing === 'none') {
            mh['autoImmuneConditions'].setValue('yes');
          }
        }
      }

      if (
        !removed.has('history.conditions') &&
        h.negatedConditions &&
        h.negatedConditions.length > 0
      ) {
        const negatedPatch: Record<string, boolean> = {};
        h.negatedConditions.forEach(key => {
          if (key === 'autoimmune') {
            if (mh['autoImmuneConditions']) {
              const existing = mh['autoImmuneConditions'].value;
              if (!existing || existing === 'none') {
                // 'none' is the literal sentinel csi-toggleable-medical-history-text-field
                // treats as "No" — any other truthy string (even 'no') is shown as "Yes"
                // with that string as the free-text detail.
                mh['autoImmuneConditions'].setValue('none');
              }
            }
          } else if (mh[key] !== undefined) {
            negatedPatch[key] = false;
          }
        });
        if (Object.keys(negatedPatch).length > 0) {
          this.medicalHistoryFormGroup.patchValue(negatedPatch);
        }
      }
    }

    const s = output.symptoms;
    if (s) {
      this.applySymptomSliders(s, skip, removed);
      const scorePatch: Record<string, number> = {};
      if (!removed.has('symptoms.osdiScore') && s.osdiScore !== null && s.osdiScore !== undefined) {
        scorePatch['osdi'] = s.osdiScore;
        scorePatch['osdi12'] = s.osdiScore;
      }
      if (
        !removed.has('symptoms.speedScore') &&
        s.speedScore !== null &&
        s.speedScore !== undefined
      ) {
        scorePatch['speed'] = s.speedScore;
      }
      if (
        !removed.has('symptoms.speedIIScore') &&
        s.speedIIScore !== null &&
        s.speedIIScore !== undefined
      ) {
        scorePatch['speedii'] = s.speedIIScore;
      }
      if (
        !removed.has('symptoms.osdi6Score') &&
        s.osdi6Score !== null &&
        s.osdi6Score !== undefined
      ) {
        scorePatch['osdi6'] = s.osdi6Score;
      }
      if (!removed.has('symptoms.deqScore') && s.deqScore !== null && s.deqScore !== undefined) {
        scorePatch['deq'] = s.deqScore;
        scorePatch['deq5'] = s.deqScore;
      }
      if (
        !removed.has('symptoms.fitzpatrickScore') &&
        s.fitzpatrickScore !== null &&
        s.fitzpatrickScore !== undefined
      ) {
        scorePatch['fitzpatrick'] = s.fitzpatrickScore;
      }
      if (Object.keys(scorePatch).length > 0) {
        this.symptomsAndSignsFormGroup.patchValue(scorePatch);
      }
    }

    this.assessmentFormGroup.controls.aiScribeResult.setValue(output.savedData);

    // Apply DEWS III differential diagnosis screening
    const dewsScreening = output.dewsScreening;
    if (
      dewsScreening &&
      this.symptomsAndSignsFormGroup.controls['dewsIIIDifferentialDiagnosisScreening']
    ) {
      const dewsFormGroup = this.symptomsAndSignsFormGroup.controls[
        'dewsIIIDifferentialDiagnosisScreening'
      ] as any;
      if (dewsFormGroup && dewsFormGroup.controls) {
        const dewsPatch: Record<string, any> = {};

        // Fields that expect string 'yes'/'no' — apply both true and false so radio buttons
        // reflect the doctor's explicit answer, not just positive findings.
        const radioFields: Array<keyof typeof dewsScreening> = [
          'suddenOnset', 'visionAffected', 'improvedByBlinking', 'frankPain', 'unilateral'
        ];
        radioFields.forEach(field => {
          if (dewsScreening[field] !== null && !removed.has('dewsScreening.' + field)) {
            dewsPatch[field] = dewsScreening[field] === true ? 'yes' : 'no';
          }
        });

        // Fields that expect boolean true/false — checkboxes; only set when explicitly stated
        if (dewsScreening.facialFlushing === true && !removed.has('dewsScreening.facialFlushing')) {
          dewsPatch['facialFlushing'] = true;
        }
        if (dewsScreening.mouthDryness === true && !removed.has('dewsScreening.mouthDryness')) {
          dewsPatch['mouthDryness'] = true;
        }
        if (
          dewsScreening.enlargedSalivaryGlands === true &&
          !removed.has('dewsScreening.enlargedSalivaryGlands')
        ) {
          dewsPatch['enlargedSalivaryGlands'] = true;
        }
        if (dewsScreening.itching === true && !removed.has('dewsScreening.itching')) {
          dewsPatch['itching'] = true;
        }
        if (dewsScreening.eyesSwollen === true && !removed.has('dewsScreening.eyesSwollen')) {
          dewsPatch['eyesSwollen'] = true;
        }
        if (dewsScreening.crustyLashes === true && !removed.has('dewsScreening.crustyLashes')) {
          dewsPatch['crustyLashes'] = true;
        }
        if (
          dewsScreening.mucopurulentDischarge === true &&
          !removed.has('dewsScreening.mucopurulentDischarge')
        ) {
          dewsPatch['mucopurulentDischarge'] = true;
        }

        if (Object.keys(dewsPatch).length > 0) {
          dewsFormGroup.patchValue(dewsPatch);
        }
      }
    }

    const effectiveRemoved = new Set([...removed, ...skip]);
    this.applyPatientProfile(output, effectiveRemoved, overwriteAll);

    this.closeScribe();
    if (this.saveFn) {
      this.saveFn().subscribe();
    }
  }

  private applyPatientProfile(
    output: ApplyOutput,
    removed: Set<string> = new Set(),
    overwriteAll: boolean = false
  ) {
    const pp = output.patientProfile;
    if (!pp || !this.patient || !this.patient.id) {
      return;
    }

    const updateInput: UpdatePatientInput = { id: this.patient.id };
    let hasUpdate = false;

    if (!removed.has('patientProfile.firstName') && pp.firstName) {
      if (overwriteAll || !this.patient.firstName) {
        updateInput.firstName = pp.firstName;
        hasUpdate = true;
      }
    }
    if (!removed.has('patientProfile.lastName') && pp.lastName) {
      if (overwriteAll || !this.patient.lastName) {
        updateInput.lastName = pp.lastName;
        hasUpdate = true;
      }
    }
    if (
      !removed.has('patientProfile.phone') &&
      pp.phoneNumber &&
      (overwriteAll || !this.patient.phone)
    ) {
      updateInput.phone = pp.phoneNumber;
      hasUpdate = true;
    }
    if (!removed.has('patientProfile.email') && pp.email && (overwriteAll || !this.patient.email)) {
      updateInput.email = pp.email;
      hasUpdate = true;
    }
    if (
      !removed.has('patientProfile.dateOfBirth') &&
      pp.dateOfBirth &&
      (overwriteAll || !this.patient.dateOfBirth)
    ) {
      updateInput.dateOfBirth = pp.dateOfBirth;
      hasUpdate = true;
    }
    if (
      !removed.has('patientProfile.address') &&
      pp.address &&
      (overwriteAll || !this.patient.address)
    ) {
      updateInput.address = pp.address;
      hasUpdate = true;
    }
    if (!removed.has('patientProfile.city') && pp.city && (overwriteAll || !this.patient.city)) {
      updateInput.city = pp.city;
      hasUpdate = true;
    }
    if (
      !removed.has('patientProfile.province') &&
      pp.province &&
      (overwriteAll || !this.patient.province)
    ) {
      updateInput.province = pp.province;
      hasUpdate = true;
    }
    if (
      !removed.has('patientProfile.postalCode') &&
      pp.postalCode &&
      (overwriteAll || !this.patient.postalCode)
    ) {
      updateInput.postalCode = pp.postalCode;
      hasUpdate = true;
    }
    if (
      !removed.has('patientProfile.country') &&
      pp.country &&
      (overwriteAll || !this.patient.country)
    ) {
      updateInput.country = pp.country;
      hasUpdate = true;
    }
    if (
      !removed.has('patientProfile.gender') &&
      pp.gender &&
      (overwriteAll || !this.patient.gender)
    ) {
      const gLower = pp.gender.toLowerCase();
      let mapped: Gender | null = null;
      if (gLower === 'male' || gLower === 'm') {
        mapped = Gender.male;
      } else if (gLower === 'female' || gLower === 'f') {
        mapped = Gender.female;
      } else if (gLower === 'other') {
        mapped = Gender.other;
      }
      if (mapped) {
        updateInput.gender = mapped;
        hasUpdate = true;
      }
    }
    const existingHC = this.patient.healthCard;
    const hasExistingHC = existingHC && existingHC.number;
    if (
      !removed.has('patientProfile.healthCard') &&
      pp.healthCardNumber &&
      (overwriteAll || !hasExistingHC)
    ) {
      updateInput.healthCard = { number: pp.healthCardNumber };
      hasUpdate = true;
    }

    if (hasUpdate) {
      this.patientService.updatePatient(updateInput).subscribe(() => {
        if (!this.patient) {
          return;
        }
        if (updateInput.firstName) {
          (this.patient as any).firstName = updateInput.firstName;
        }
        if (updateInput.lastName) {
          (this.patient as any).lastName = updateInput.lastName;
        }
        if (updateInput.phone) {
          this.patient.phone = updateInput.phone;
        }
        if (updateInput.email) {
          this.patient.email = updateInput.email;
        }
        if (updateInput.dateOfBirth) {
          this.patient.dateOfBirth = updateInput.dateOfBirth;
        }
        if (updateInput.address) {
          this.patient.address = updateInput.address;
        }
        if (updateInput.city) {
          this.patient.city = updateInput.city;
        }
        if (updateInput.province) {
          this.patient.province = updateInput.province;
        }
        if (updateInput.postalCode) {
          this.patient.postalCode = updateInput.postalCode;
        }
        if (updateInput.country) {
          this.patient.country = updateInput.country;
        }
        if (updateInput.gender) {
          this.patient.gender = updateInput.gender;
        }
        if (updateInput.healthCard) {
          if (!this.patient.healthCard) {
            (this.patient as any).healthCard = { number: updateInput.healthCard.number };
          } else {
            this.patient.healthCard.number = updateInput.healthCard.number;
          }
        }
      });
    }

    this.applyPatientFile(output.patientFile, output.history, removed, overwriteAll);
  }

  private applyPatientFile(
    pf: PatientFileExtraction | null | undefined,
    history: HistoryExtraction | null | undefined,
    removed: Set<string> = new Set(),
    overwriteAll: boolean = false
  ) {
    if (!this.patient || !this.patient.id) {
      return;
    }

    const scalarFields: string[] = [
      'wearsGlasses',
      'eyeColor',
      'sunTanExposureFrequency',
      'lastSunExposure',
      'longSunExposure',
      'darkerAfterSunExposure',
      'skinColor',
      'naturalHairColor',
      'faceSunSensitivity',
      'freckles',
      'tanIntensity',
      'ethnicity',
      'education',
      'eyelashModification',
      'sickBuildingSyndrome',
      'styeOrChalazion',
      'childBirth',
      'hematopoieticStemCellTransplantation'
    ];

    const mh =
      this.medicalHistoryFormGroup &&
      (this.medicalHistoryFormGroup.controls as Record<string, any>);
    const mhFields: string[] = [
      'wearsGlasses',
      'eyeColor',
      'sunTanExposureFrequency',
      'lastSunExposure',
      'longSunExposure',
      'darkerAfterSunExposure',
      'skinColor',
      'naturalHairColor',
      'faceSunSensitivity',
      'freckles',
      'tanIntensity',
      'ethnicity',
      'education',
      'eyelashModification',
      'sickBuildingSyndrome',
      'styeOrChalazion'
    ];
    if (pf && mh) {
      mhFields.forEach(key => {
        if (removed.has('patientFile.' + key)) {
          return;
        }
        const val = ((pf as unknown) as Record<string, string | null>)[key];
        if (val && mh[key] && (overwriteAll || !mh[key].value)) {
          mh[key].setValue(val);
        }
      });
    }

    const existing: Record<string, unknown> = this.patient.nonModifiableData
      ? JSON.parse(this.patient.nonModifiableData)
      : {};
    let changed = false;

    if (pf) {
      scalarFields.forEach(key => {
        if (removed.has('patientFile.' + key)) {
          return;
        }
        const val = ((pf as unknown) as Record<string, string | null>)[key];
        if (val && (overwriteAll || !existing[key])) {
          existing[key] = val;
          changed = true;
        }
      });

      if (
        !removed.has('patientFile.familyHistory') &&
        pf.familyHistory &&
        !existing['familyHistory']
      ) {
        existing['familyHistory'] = pf.familyHistory;
        changed = true;
      }

      if (!removed.has('patientFile.gender') && pf.gender && pf.gender.length) {
        pf.gender.forEach(function(itemKey) {
          if (!existing[itemKey]) {
            existing[itemKey] = true;
            changed = true;
          }
        });
      }
      if (
        !removed.has('patientFile.pastOcularHistory') &&
        pf.pastOcularHistory &&
        pf.pastOcularHistory.length
      ) {
        pf.pastOcularHistory.forEach(function(itemKey) {
          if (!existing[itemKey]) {
            existing[itemKey] = true;
            changed = true;
          }
        });
      }
      if (
        !removed.has('patientFile.pastSurgeries') &&
        pf.pastSurgeries &&
        pf.pastSurgeries.length
      ) {
        pf.pastSurgeries.forEach(function(itemKey) {
          if (!existing[itemKey]) {
            existing[itemKey] = true;
            changed = true;
          }
        });
      }
    }

    if (
      !removed.has('patientFile.medicalConditions') &&
      history &&
      history.conditions &&
      history.conditions.length
    ) {
      history.conditions.forEach(function(condKey) {
        if (!existing[condKey]) {
          existing[condKey] = true;
          changed = true;
        }
      });
    }

    if (changed) {
      const newNonModData = JSON.stringify(existing);
      this.patient.nonModifiableData = newNonModData;
      this.patientService
        .updatePatient({ id: this.patient.id, nonModifiableData: newNonModData })
        .subscribe();
    }
  }

  private severityToSlider(level: string): number {
    const map: Record<string, number> = { none: 0, mild: 30, moderate: 50, severe: 70 };
    return map[level] !== undefined ? map[level] : 0;
  }

  private applySymptomSliders(
    s: SymptomsExtraction,
    skip: Set<string> = new Set(),
    removed: Set<string> = new Set()
  ) {
    const mh = this.medicalHistoryFormGroup.controls as Record<string, any>;
    const setSlider = (key: string, value: number, removedKey?: string) => {
      if (mh[key] && !skip.has(key) && (!removedKey || !removed.has(removedKey))) {
        mh[key].setValue(value);
      }
    };
    if (s.frequency) {
      setSlider('symptomFrequency', this.severityToSlider(s.frequency), 'symptoms.frequency');
    }

    if (s.severity) {
      setSlider('symptomSeverity', this.severityToSlider(s.severity), 'symptoms.severity');
    } else if (s.osdiScore !== null && s.osdiScore !== undefined) {
      setSlider('symptomSeverity', this.osdiScoreToSlider(s.osdiScore), 'symptoms.severity');
    } else if (s.speedScore !== null && s.speedScore !== undefined) {
      setSlider('symptomSeverity', this.speedScoreToSlider(s.speedScore), 'symptoms.severity');
    }

    if (s.itchiness) {
      setSlider('eyeItchiness', this.severityToSlider(s.itchiness), 'symptoms.itchiness');
    }
    if (s.tearing) {
      setSlider('eyeTearingQuantity', this.severityToSlider(s.tearing), 'symptoms.tearing');
    }

    const setRadio = (key: string, value: string | null | undefined, removedKey?: string) => {
      if (value && mh[key] && !skip.has(key) && (!removedKey || !removed.has(removedKey))) {
        mh[key].setValue(value);
      }
    };
    setRadio('tearingQuality', s.tearingQuality, 'symptoms.tearingQuality');
    setRadio('sleepingPosition', s.sleepingPosition, 'symptoms.sleepingPosition');
    setRadio('eyeRubbingFrequency', s.eyeRubbingFrequency, 'symptoms.eyeRubbing');
    setRadio('bothersomeEye', s.bothersomeEye, 'symptoms.bothersomeEye');
    setRadio('screenTime', s.screenTime, 'symptoms.screenTime');
    setRadio('symptomSeverityOccurrence', s.symptomSeverityOccurrence, 'symptoms.symptomsWorstAt');
  }

  private osdiScoreToSlider(score: number): number {
    if (score >= 33) {
      return 70;
    }
    if (score >= 23) {
      return 50;
    }
    if (score >= 13) {
      return 30;
    }
    return 0;
  }

  private speedScoreToSlider(score: number): number {
    if (score >= 12) {
      return 70;
    }
    if (score >= 7) {
      return 50;
    }
    return 30;
  }

  private matchTreatmentByName(name: string, candidates: Treatment[]): Treatment | null {
    if (!name || !candidates.length) {
      return null;
    }
    const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const n = norm(name);
    if (n.length < 2) {
      return null;
    }

    let found = candidates.find(t => norm(t.name) === n);
    if (found) {
      return found;
    }
    found = candidates.find(t => norm(t.id) === n);
    if (found) {
      return found;
    }
    found = candidates.find(t => {
      const tn = norm(t.name);
      return tn.length >= 4 && n.startsWith(tn);
    });
    if (found) {
      return found;
    }
    found = candidates.find(t => {
      const tn = norm(t.name);
      return n.length >= 4 && tn.startsWith(n);
    });
    if (found) {
      return found;
    }
    found = candidates.find(t => {
      const tn = norm(t.name);
      return tn.length >= 5 && n.includes(tn);
    });
    if (found) {
      return found;
    }
    const nameWords = n.split(/\s+/);
    found = candidates.find(t =>
      nameWords.every(word => word.length >= 2 && (t.searchText || '').includes(word))
    );
    if (found) {
      return found;
    }
    return null;
  }

  private mergeIntoTreatmentControl(ctrl: any, incoming: Treatment[]) {
    if (!ctrl || !incoming.length) {
      return;
    }
    const existing: Treatment[] = ctrl.value || [];
    const existingIds = new Set(existing.map((t: Treatment) => t.id));
    const toAdd = incoming.filter(t => !existingIds.has(t.id));
    if (toAdd.length) {
      ctrl.setValue([...existing, ...toAdd]);
    }
  }

  private applyMedicationsFromScribe(
    medications: string,
    controlKey: string = 'medications'
  ): void {
    const names = medications
      .split(/[,;]+/)
      .map(n => n.trim())
      .filter(n => n.length >= 2);
    if (!names.length) {
      return;
    }
    forkJoin(
      names.map(name => this.drugbankLookupService.searchMedication({ query: name }).pipe(take(1)))
    ).subscribe(results => {
      const matched: Drug[] = results
        .map((drugs, index) => {
          if (!drugs || !drugs.length) return null;
          const aiMedString = names[index];
          return this.findBestDrugMatch(drugs, aiMedString);
        })
        .filter((d): d is Drug => d !== null);
      if (!matched.length) {
        return;
      }
      const ctrl = this.medicalHistoryFormGroup.controls[controlKey];
      if (!ctrl) {
        return;
      }
      const existing: Drug[] = Array.isArray(ctrl.value) ? ctrl.value : [];
      const existingIds = new Set(existing.map((d: Drug) => d.id));
      const toAdd = matched.filter(d => !existingIds.has(d.id));
      if (toAdd.length) {
        ctrl.setValue([...existing, ...toAdd]);
      }
    });
  }

  private findBestDrugMatch(drugs: Drug[], aiMedString: string): Drug | null {
    if (!drugs || drugs.length === 0) return null;
    if (drugs.length === 1) return drugs[0];

    const aiLower = aiMedString.toLowerCase();
    const dosageMatch = aiLower.match(/(\d+)\s*(mg|mcg|g|ml)/i);
    const aiDosage = dosageMatch ? parseInt(dosageMatch[1]) : null;
    const aiUnit = dosageMatch ? dosageMatch[2].toLowerCase() : null;
    const hasER = /\b(er|extended.release|xr|sr|cr)\b/i.test(aiLower);
    const hasIR = /\b(ir|immediate.release)\b/i.test(aiLower);

    const scoredDrugs = drugs.map(drug => {
      let score = 0;

      if (aiDosage && drug.active_ingredients && drug.active_ingredients.length > 0) {
        const ingredient = drug.active_ingredients[0];
        if (ingredient && ingredient.strength === aiDosage) {
          score += 100;
          if (aiUnit && ingredient.strength_unit) {
            const drugUnit = ingredient.strength_unit.toLowerCase();
            if (drugUnit === aiUnit || drugUnit.includes(aiUnit) || aiUnit.includes(drugUnit)) {
              score += 50;
            }
          }
        } else if (ingredient && ingredient.strength) {
          const ratio =
            Math.max(ingredient.strength, aiDosage) / Math.min(ingredient.strength, aiDosage);
          if (ratio <= 2) {
            score += 20;
          }
        }
      }

      if (drug.dosage_form) {
        const formLower = drug.dosage_form.toLowerCase();
        if (hasER && /\b(er|extended|xr|sr|cr)\b/i.test(formLower)) {
          score += 75;
        } else if (hasIR && /\b(ir|immediate)\b/i.test(formLower)) {
          score += 75;
        } else if (
          !hasER &&
          !hasIR &&
          !/\b(er|extended|xr|sr|cr|ir|immediate)\b/i.test(formLower)
        ) {
          score += 25;
        } else if (hasER && !/\b(er|extended|xr|sr|cr)\b/i.test(formLower)) {
          score -= 50;
        }
      } else if (!hasER && !hasIR) {
        score += 10;
      }

      if (drug.name) {
        const drugNameLower = drug.name.toLowerCase();
        const aiFirstWord = aiLower.split(/\s+/)[0];
        if (drugNameLower.includes(aiFirstWord) || aiFirstWord.includes(drugNameLower)) {
          score += 10;
        }
      }

      return { drug, score };
    });

    scoredDrugs.sort((a, b) => b.score - a.score);
    return scoredDrugs[0].score > 0 ? scoredDrugs[0].drug : drugs[0];
  }

  private toTitleCase(text: string): string {
    if (!text) {
      return text;
    }
    return text.replace(/\b\w/g, c => c.toUpperCase());
  }

  // The Impressions control can contain HTML fragments injected by the CDS
  // "Interpret" / "Risk Factors" features (see upsertImpressionsSection) alongside
  // the doctor's plain text. Strip tags before showing it as plain text in the AI
  // Scribe conflict/review UI, or the raw markup renders as literal visible text.
  private stripHtml(html: string): string {
    if (!html) {
      return html;
    }
    const div = document.createElement('div');
    div.innerHTML = html;
    return (div.textContent || div.innerText || '').trim();
  }

  private parseSymptomDuration(
    text: string
  ): { year: number; month: number; total: number } | null {
    if (!text) {
      return null;
    }
    const t = text.toLowerCase();
    const wordMap: Record<string, number> = {
      zero: 0,
      one: 1,
      two: 2,
      three: 3,
      four: 4,
      five: 5,
      six: 6,
      seven: 7,
      eight: 8,
      nine: 9,
      ten: 10,
      eleven: 11,
      twelve: 12
    };
    const parseNum = (s: string): number => {
      const n = parseInt(s, 10);
      return !isNaN(n) ? n : wordMap[s] !== undefined ? wordMap[s] : 0;
    };
    const yearMatch = t.match(/(\w+)\s+year/);
    const monthMatch = t.match(/(\w+)\s+month/);
    const year = yearMatch ? parseNum(yearMatch[1]) : 0;
    const month = monthMatch ? parseNum(monthMatch[1]) : 0;
    if (year === 0 && month === 0) {
      return null;
    }
    return { year, month, total: year * 12 + month };
  }

  private parseTextToDate(text: string): Date | null {
    if (!text) {
      return null;
    }
    const d = new Date(text);
    if (!isNaN(d.getTime())) {
      return d;
    }
    const monthYearMatch = text.match(/^([A-Za-z]+)\s+(\d{4})$/);
    if (monthYearMatch) {
      const parsed = new Date(monthYearMatch[1] + ' 1, ' + monthYearMatch[2]);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }

    const now = new Date();
    const lower = text.toLowerCase().trim();

    const yearsAgoMatch = lower.match(/^(?:a|an|(\d+(?:\.\d+)?))\s+year[s]?\s+ago$/);
    if (yearsAgoMatch) {
      const years = yearsAgoMatch[1] ? parseFloat(yearsAgoMatch[1]) : 1;
      const result = new Date(now);
      result.setMonth(result.getMonth() - Math.round(years * 12));
      return result;
    }

    const monthsAgoMatch = lower.match(/^(?:a|an|(\d+(?:\.\d+)?))\s+month[s]?\s+ago$/);
    if (monthsAgoMatch) {
      const months = monthsAgoMatch[1] ? parseFloat(monthsAgoMatch[1]) : 1;
      const result = new Date(now);
      result.setMonth(result.getMonth() - Math.round(months));
      return result;
    }

    const weeksAgoMatch = lower.match(/^(?:a|an|(\d+))\s+week[s]?\s+ago$/);
    if (weeksAgoMatch) {
      const weeks = weeksAgoMatch[1] ? parseInt(weeksAgoMatch[1], 10) : 1;
      const result = new Date(now);
      result.setDate(result.getDate() - weeks * 7);
      return result;
    }

    if (lower === 'last year') {
      const result = new Date(now);
      result.setFullYear(result.getFullYear() - 1);
      return result;
    }
    if (lower === 'last month') {
      const result = new Date(now);
      result.setMonth(result.getMonth() - 1);
      return result;
    }

    const compoundMatch = lower.match(/(\d+)\s+year[s]?\s+and\s+(\d+)\s+month[s]?\s+ago/);
    if (compoundMatch) {
      const result = new Date(now);
      result.setFullYear(result.getFullYear() - parseInt(compoundMatch[1], 10));
      result.setMonth(result.getMonth() - parseInt(compoundMatch[2], 10));
      return result;
    }

    return null;
  }
}
